<!--
    The Vertrag basics: a band of figures, or — at xs — two lines of running text.

    Both forms print the server's values verbatim. Nothing here is rounded, re-derived,
    re-formatted or joined with an invented connective.
-->
@if (running) {

    <!--
        Running text. The separator is a real element with its own margins, NOT the template's
        indentation: Angular drops whitespace-only text nodes between elements, so a "·" that relied
        on the source layout would render glued to the value beside it.
    -->
    <div class="surface-100 border-round px-3 py-2 text-sm">
        @for (line of runningLines; track $index) {
            <div class="cm-ed-band-line line-height-3">
                @for (field of line; track $index) {
                    @if (!$first) {
                        <span class="text-color-secondary mx-1" aria-hidden="true">·</span>
                    }
                    <!--
                        The label is the value's title here: at this width it costs more room than it
                        earns.

                        THE SEARCH ONLY ADDS. A matching figure takes the sheet's own highlight pair —
                        `bg-primary-50` on the figure, and the sheet's one `.cm-ed-mark` treatment on
                        the matched run — and every other figure renders exactly as it does with no
                        term. Nothing is dimmed, hidden or reordered, so the running line reads the
                        same either way. The run names no colour here: it carries its whole appearance
                        in the shared partial, so this band cannot drift from the pairs beside it.

                        The runs come from the HOST (`field.valueSegments`); this band folds nothing
                        and compares nothing, so it can never mark a different set of characters than
                        the header counted. Every run is wrapped in an ELEMENT rather than emitted as a
                        bare interpolation, because a bare one shares its text node with the template's
                        indentation and the compiler would collapse that into a space inside a
                        highlighted word.
                    -->
                    <span class="cm-ed-figure font-medium text-color"
                          [class.bg-primary-50]="searching && field.searchHit"
                          [class.border-round-sm]="searching && field.searchHit"
                          [title]="field.label">@if (searching && field.valueSegments?.length) {
                            @for (segment of field.valueSegments; track $index) {
                                @if (segment.hit) {
                                    <mark class="cm-ed-mark">{{segment.text}}</mark>
                                } @else {
                                    <span>{{segment.text}}</span>
                                }
                            }
                        } @else {
                            <span>{{field.displayValue}}</span>
                        }</span>

                    <!--
                        The figure matched on something this line does not print — its label (dropped
                        at this width), its raw form, or one of the technical members. Without the
                        marker the wash would have no visible cause, which on the one screen built to
                        prove nothing is hidden reads as a rendering fault rather than as a hit.
                    -->
                    @if (searching && field.searchHit && !valueHit(field)) {
                        <span class="cm-ed-tech-hit bg-primary-50 text-primary border-1 border-primary border-round-sm ml-1"
                              title="Der Suchbegriff wurde nicht im angezeigten Wert gefunden, sondern in der Bezeichnung oder in den technischen Daten dieses Feldes.">Treffer</span>
                    }
                    @if (field.unit) {
                        <span class="text-color-secondary ml-1">{{field.unit}}</span>
                    }
                    <!--
                        The qualifier stays even here, where the LABEL was dropped: a label can be
                        recovered from the title attribute, a Zahlungsweise cannot be recovered from
                        anything on this line. "212,60 €" and "212,60 € monatlich" are two different
                        facts, and this band is the only Vertrag rendering V3 has.
                    -->
                    @if (field.dto.qualifier) {
                        <span class="cm-ed-band-q text-color-secondary ml-1">{{field.dto.qualifier}}</span>
                    }
                }
            </div>
        }
    </div>

} @else {

    <!--
        The band of figures.

        `surface-100` and not `surface-ground`: the band sits ON the page, and the page is already
        drawn in surface-ground — the band was invisible against it. surface-100 is the sheet's own
        subtle section background, the same token the ÜBERBLICK strip and the card header bands wear,
        so the three read as one system. Never a new colour value.

        The FIGURES stay `text-color` + `font-semibold` and the labels `text-color-secondary
        text-xs`: the weight and the size already rank them, and a band of four coloured figures
        would rank none of them. In particular the premium is NOT singled out — see the component's
        TSDoc for why that cannot be derived from this payload.

        overflow-hidden is the clip the divider rule below depends on — see .cm-ed-band.
    -->
    <div class="surface-100 border-round px-3 py-2 overflow-hidden">

        <!--
            align-items-START, so every cell begins at the same TOP edge: all labels sit on one line
            and all figures on the line beneath them, right across the band.

            This was align-items-end, which lined the cells up by their BOTTOM edge. The Beitrag cell
            carries a THIRD line — the qualifier, "monatlich, Lastschrift" — so its bottom edge is
            that qualifier, and aligning on it lifted the Beitrag label AND its figure a full line
            above the rest of the band: "Beitrag" sat higher than "Versicherungsnummer" and
            "212,60 €" no longer shared the line of "5426/8 810 442". The rule was written to survive
            a WRAPPING label, but every label here carries white-space-nowrap and
            text-overflow-ellipsis and cannot wrap — so the case it protected against cannot occur,
            while the misalignment it caused was on screen in every delivery with a Zahlungsweise.
            Do not restore it: with the top edge shared, the qualifier simply hangs below the one
            cell that has one, which is where it belongs.

            The divider is a border-left on EVERY cell, including the first, and the negative left
            margin pushes that first border out under the panel's own edge where overflow-hidden
            clips it. That is what keeps a WRAPPED row clean: with the border merely omitted on
            `$first`, only the very first cell of the band lost it and every row after a wrap opened
            with a stray divider — and this band wraps at `sm` and `md` as a matter of course.
        -->
        <div class="cm-ed-band flex flex-wrap align-items-start gap-3">
            @for (field of fields; track $index) {
                <!--
                    THE SEARCH HIGHLIGHT — background and radius only, exactly as on a pair of
                    `mvs-cm-ed-kv-list`, so a hit looks the same in the band as it does everywhere
                    else. No border and no padding is added: the cell's divider and its 1rem of
                    left padding carry the band's whole rhythm, and switching a border on while the
                    reader is still typing would shove every following cell sideways.

                    There is NO counterpart for a cell that did not match: no dim, no opacity, no
                    grey. The wash is the only thing a search changes here.
                -->
                <div class="cm-ed-band-cell flex flex-column min-w-0 border-left-1 surface-border pl-3"
                     [class.bg-primary-50]="searching && field.searchHit"
                     [class.border-round-sm]="searching && field.searchHit">

                    <!--
                        The label carries highlight runs of its own: the band is the card-led view's
                        only Vertrag rendering, and a reader searching for a basic by NAME
                        ("Versicherungsnummer") is the ordinary case here, not the exotic one.
                    -->
                    <span class="text-xs text-color-secondary overflow-hidden text-overflow-ellipsis white-space-nowrap"
                          [title]="field.label">@if (searching && field.labelSegments?.length) {
                            @for (segment of field.labelSegments; track $index) {
                                @if (segment.hit) {
                                    <mark class="cm-ed-mark">{{segment.text}}</mark>
                                } @else {
                                    <span>{{segment.text}}</span>
                                }
                            }
                        } @else {
                            <span>{{field.label}}</span>
                        }</span>

                    <!--
                        The figure and its unit, cut rather than wrapped — the same three utilities
                        the label above already relies on, on the same kind of element (a flex item
                        of this column, so it is block-level and text-overflow applies).

                        A cell is bounded (see .cm-ed-band-cell), and a value that WRAPPED inside it
                        would add a second line to that one cell and push the band's rows apart —
                        the very defect align-items-end produced, arriving by another route. min-w-0
                        on the cell is what lets the cut happen at all: a flex child refuses to
                        shrink below its content without it, so the ellipsis alone would do nothing.

                        title carries the line back verbatim, value and unit exactly as rendered, so
                        a cut figure is still readable in full — this screen exists to prove nothing
                        went missing, and a truncation with no way back would do the opposite.
                    -->
                    <span class="cm-ed-figure font-semibold text-color overflow-hidden text-overflow-ellipsis white-space-nowrap"
                          [ngClass]="figureClass"
                          [title]="figureTitle(field)">
                        @if (searching && field.valueSegments?.length) {
                            @for (segment of field.valueSegments; track $index) {
                                @if (segment.hit) {
                                    <mark class="cm-ed-mark">{{segment.text}}</mark>
                                } @else {
                                    <span>{{segment.text}}</span>
                                }
                            }
                        } @else {
                            <span>{{field.displayValue}}</span>
                        }
                        @if (field.unit) {
                            <span class="text-sm font-normal text-color-secondary ml-1">{{field.unit}}</span>
                        }
                    </span>

                    <!--
                        The cell matched on something it does not print — the raw delivered form, the
                        unit, the GDV coordinate, the source entity, the row id or the type-field id.
                        Stated for the same reason as on a pair: a wash whose cause is nowhere in the
                        cell reads as a defect on the one screen built to prove nothing is hidden.
                    -->
                    @if (searching && field.techHit) {
                        <span class="cm-ed-tech-hit bg-primary-50 text-primary border-1 border-primary border-round-sm align-self-start"
                              title="Der Suchbegriff wurde nur in den technischen Daten dieses Feldes gefunden (Koordinate, Entität, Datensatz-Nr., Feld-Id).">Techn. Treffer</span>
                    }

                    <!--
                        The qualifier, UNDER the figure and never beside it: it is the condition the
                        figure holds under ("monatlich, Lastschrift"), and beside a bold figure at
                        this size it would read as part of the amount. Its own line costs the band
                        nothing now that the cells share a TOP edge — it hangs below this one cell
                        and leaves every label and every figure where they were.
                    -->
                    @if (field.dto.qualifier) {
                        <span class="cm-ed-band-q text-color-secondary">{{field.dto.qualifier}}</span>
                    }

                </div>
            }
        </div>

    </div>
}
